/**
 * Sound effects and lobby music. Files live in `web/public/audio/` (`client/audio/` is the untouched source).
 * Volume and mute come from the player's options (`configureAudio`); each play clones the
 * preloaded element so overlapping sounds don't cut each other off.
 *
 * Music (`daybreak.mp3`) loops seamlessly (Web Audio, see below) while `setMusicWanted(true)` (the main lobby and a game's lobby), with
 * its own volume and on/off; `muted` silences it too. Browsers block autoplay until the first click
 * or key, so a blocked start is retried then.
 */
export const SOUNDS = {
  collide: 'collide.mp3',
  stomp: 'stomp.mp3',
  energyBlastImpact: 'energy_blast_impact.mp3',
  roundStart: 'round_start.wav',
  roundEnd: 'round_end.mp3',
  /** A private chat message to you. */
  im: 'im.mp3',
  /** A player (not a bot) joins your game's lobby. */
  buddyIn: 'buddyin.mp3',
  /** A Ka'Dice is rolled (its splash). */
  kadicePower: 'kadice_power.mp3',
  /** You killed more than one card in one attack. */
  niceJob: 'nice_job.mp3',
  botHa: 'ha.mp3',
  botEvilLaugh: 'evil_laugn.mp3',
  botEvilLaugh2: 'evil_laugh_2.mp3',
  botDie: 'die.mp3',
  botDead: 'dead.mp3',
  botNothing: 'you_are_nothing.mp3',
  botHelpless: 'you_are_helpless.mp3',
  /** Beginner Mode: enemies light up as attackable. */
  destroyIt: 'destroy_it.mp3',
  chicken: 'chicken.mp3',
  chicken2: 'chicken2.mp3',
  coward: 'coward.mp3',
} as const;
export type SoundId = keyof typeof SOUNDS;

/** One of these plays (picked at random) when a bot kills a player. */
const BOT_KILL_SOUNDS: SoundId[] = [
  'botHa',
  'botEvilLaugh',
  'botEvilLaugh2',
  'botDie',
  'botDead',
  'botNothing',
  'botHelpless',
];
export const botKillSound = (): SoundId => BOT_KILL_SOUNDS[Math.floor(Math.random() * BOT_KILL_SOUNDS.length)]!;

/** One of these plays (picked at random) when you forfeit. */
const FORFEIT_SOUNDS: SoundId[] = ['chicken', 'chicken2', 'coward'];
export const forfeitSound = (): SoundId => FORFEIT_SOUNDS[Math.floor(Math.random() * FORFEIT_SOUNDS.length)]!;

const cache = new Map<SoundId, HTMLAudioElement>();
let volume = 0.7;
let muted = false;

const load = (id: SoundId) => {
  let el = cache.get(id);
  if (!el) {
    el = new Audio(`/audio/${SOUNDS[id]}`);
    el.preload = 'auto';
    cache.set(id, el);
  }
  return el;
};

// Warm the cache so the first impact isn't late.
if (typeof Audio !== 'undefined') (Object.keys(SOUNDS) as SoundId[]).forEach(load);

const MUSIC = '/audio/daybreak.mp3';
let musicWanted = false;
let musicOn = true;
let musicVolume = 0.6;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

export function configureAudio(opts: { volume: number; muted: boolean; music: boolean; musicVolume: number }) {
  volume = clamp01(opts.volume);
  muted = opts.muted;
  musicOn = opts.music;
  musicVolume = clamp01(opts.musicVolume);
  syncMusic();
}

/** Whether the screen calls for lobby music. Whether it plays also depends on the options. */
export function setMusicWanted(wanted: boolean) {
  musicWanted = wanted;
  syncMusic();
}

/*
 * Music plays through Web Audio, not an <audio loop> element: the element restarts with a gap
 * (MP3 encoder padding plus seek latency), while a looping AudioBufferSourceNode wraps
 * sample-accurately (decodeAudioData drops the LAME padding). Stopping suspends the context, so
 * it resumes where it left off.
 */
let ctx: AudioContext | null = null;
let gain: GainNode | null = null;
let started = false;

function startMusic() {
  if (started || !ctx || !gain) return;
  started = true;
  const c = ctx;
  const g = gain;
  fetch(MUSIC)
    .then((r) => r.arrayBuffer())
    .then((data) => c.decodeAudioData(data))
    .then((buffer) => {
      const src = c.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.connect(g);
      src.start();
    })
    .catch(() => {
      started = false; // retried on the next sync
    });
}

function syncMusic() {
  if (typeof AudioContext === 'undefined') return;
  const play = musicWanted && musicOn && !muted && musicVolume > 0;
  if (!play) {
    if (ctx?.state === 'running') ctx.suspend().catch(() => {});
    return;
  }
  if (!ctx) {
    ctx = new AudioContext();
    gain = ctx.createGain();
    gain.connect(ctx.destination);
  }
  gain!.gain.value = musicVolume;
  startMusic();
  if (ctx.state !== 'running') ctx.resume().catch(() => {}); // blocked: retried on the first click or key
}

if (typeof document !== 'undefined') {
  const unlock = () => {
    syncMusic();
    if (ctx?.state === 'running') {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    }
  };
  document.addEventListener('pointerdown', unlock);
  document.addEventListener('keydown', unlock);
}

/** The sound effects volume right now, 0 when muted (for sounds made in code: `dice3d/sfx.ts`). */
export const sfxLevel = (): number => (muted ? 0 : volume);

export function playSound(id: SoundId) {
  if (muted || volume === 0 || typeof Audio === 'undefined') return;
  const el = load(id).cloneNode() as HTMLAudioElement;
  el.volume = volume;
  // Autoplay can be blocked until the first click; a missed sound is not worth an error.
  el.play().catch(() => {});
}
